import {EventType, ProtectionNameAnalytic} from '@src/dtos/analytics.dto';
import {RecommendedAppData} from '@src/dtos/recommended-app.dto';
import {Step} from '@src/model/step';
import {versionStatusAndStateMap} from '@src/model/version';
import {MlRecommendationData} from '@src/services/ml-recommendation.service';
import {
    BulkRecommendationType,
    BulkUpdateAction,
    BulkUpdateDirection,
    BulkUpdateType
} from '@src/utils/bulkUpdateUtils';
import {mapToArray} from '@src/utils/map-and-array';
import {RuleHelper} from '@src/utils/rule-helper';
import {toLowerWithDivider} from '@src/utils/string-normalization';
import {SystemConfiguration} from '@src/utils/system-configuration';
import {
    DRAFT_STATUSES,
    getAddressGroupsByVersion,
    getAddressGroupsByVersionAndAddress,
    getBlockCategoriesListByVersion,
    getRuleById,
    getRulesByVersionAndTypeOptional,
    isVersionActive,
    isVersionDraft,
    isVersionInactive,
    mapAddresses,
    mapDomains
} from '@src/utils/versionUtils';
import isEqual from 'lodash/isEqual';
import {action, computed, makeObservable, observable, reaction, runInAction} from 'mobx';
import moment from 'moment';
import { AppDto, AppWithStatus } from 'src/dtos/app.dto';
import {ServicesUpdateDto} from 'src/dtos/default-service-update.dto';
import {
    Address,
    AddressGroup,
    Domain,
    FwVersion,
    FwVersionContent,
    FwVersionContentAndMetadata,
    FwVersionInfo,
    IP,
    Rule,
    Service
} from 'src/dtos/version.dto';
import fwVersionService from 'src/services/fw-version.service';
import managedAppsService from 'src/services/managed-apps.service';
import {calcBlockInfo} from 'src/utils/blockInfo';
import {
    calcActionDuration,
    LAST_VISITED_PROTECTION_TAB,
    LOADER_MINIMUM_MS,
    PUSH_POLICY_BATCH_ID,
} from 'src/utils/constants';
import {delayPromise} from 'src/utils/delay';
import {
    BlockDirectionType,
    Direction,
    DisableReasons,
    InventorySubMenu,
    IPControlSubMenu,
    LevelName,
    PolicyType,
    ProtectionMenuNameOptions,
    ProtectionPageName,
    SavingStatus,
    VersionState,
    VersionStatus,
    WebFilteringSubMenu
} from 'src/utils/enums';
import {StepList} from '../features/protection-assistant/stepsList';
import {PolicyActionDto} from '../features/version-button/policy-action-modal';
import {PolicyMenuItem} from '../shared/bulk-options/bulk-policy-update-items';
import {BaseStore} from './base.store';
import {RootStore} from './index';

export class FwVersionStore extends BaseStore {
    public fwVersions: FwVersionInfo[] | undefined = undefined;
    public activeVersionData: FwVersion | undefined = undefined;
    public selectedFwVersion: FwVersion | undefined = undefined;
    public countriesWithStatusMap: Record<string, Record<string, boolean>> = {};
    public isLoading = false;
    public isDirty = false;
    public savingStatus: SavingStatus | undefined = undefined;
    public protectionType: ProtectionMenuNameOptions = localStorage.getItem(LAST_VISITED_PROTECTION_TAB) as ProtectionMenuNameOptions || ProtectionMenuNameOptions.GEO_FILTERING;
    public ipControlType: IPControlSubMenu = IPControlSubMenu.ALLOW_LIST;
    public webFilteringType: WebFilteringSubMenu = WebFilteringSubMenu.CATEGORIES;
    public inventoryType: InventorySubMenu = InventorySubMenu.ADDRESSES;
    public firewallRulesMode: 'view' | 'new' | 'edit' = 'view';
    public protAsstActIdx = -1;
    public stepsCount: number = StepList.length;
    public protAsstSteps: Step[] = StepList.map(step => new Step(step));
    public baseVersion: FwVersion | undefined = undefined;
    public containsActiveVersions: boolean | undefined = undefined;
    public newRulePosition: number | undefined = undefined;
    private applyPolicyTime: number | undefined = undefined;
    public allFwVersions: Record<number, FwVersionInfo[] | undefined> = {};
    public recommendedAppData: RecommendedAppData | undefined = undefined;
    public mlRecommendationData: MlRecommendationData | undefined = undefined;
    public batchId: string | null = localStorage.getItem(PUSH_POLICY_BATCH_ID);
    public aiApps: AppDto[] = [];

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            loadActiveVersionDataWhenVersionsChanges: action,
            activeVersionData: observable,
            fwVersions: observable,
            isLoading: observable,
            savingStatus: observable,
            selectedFwVersion: observable,
            protectionType: observable,
            ipControlType: observable,
            webFilteringType: observable,
            inventoryType: observable,
            firewallRulesMode: observable,
            updateCountryStatus: action,
            isSelectedVersionCommittable: computed,
            isNotDraftExists: computed,
            draftVersion: computed,
            pendingVersion: computed,
            getActiveVersion: computed,
            isSelectedVersionEditable: computed,
            selectedVersionId: computed,
            versionStateLabel: computed,
            latestVersion: computed,
            selectedVersionAddressGroups: computed,
            getDisableReason: computed,
            selectedVersionAddressGroupsByAddress: action,
            selectedVersionRules: computed,
            urlFilteringBlockCategoriesList: computed,
            getProtectionPlusFeatures: computed,
            isDirty: observable,
            setIsDirty: action,
            setProtectionType: action,
            setIpControlType: action,
            setWebFilteringType: action,
            setInventoryType: action,
            setFirewallRulesMode: action,
            getAllowList: computed,
            getBlockList: computed,
            getIpList: computed,
            getDomains: computed,
            getAddresses: computed,
            getPureCustomServices: computed,
            versionProtectionPath: computed,
            isInitialVersion: computed,
            protAsstActIdx: observable,
            protAsstSteps: observable,
            isOneProtAsstStepNotSame: computed,
            isProtAsstOpen: computed,
            isAllProtAsstStepsDone: computed,
            rulesCount: computed,
            customRulesCount: computed,
            isDisabledProtectionTypeOnProtAsst: observable,
            createNewVersionWithAllRecommendations: observable,
            containsActiveVersions: observable,
            newRulePosition: observable,
            countriesWithStatusMap: observable,
            managedApps: computed,
            isOneVersionActive: computed,
            isSecondVersionInDraft: computed,
            isSelectedVersionExist: computed,
            allFwVersions: observable,
            recommendedAppData: observable,
            setRecommendedAppData: action,
            isVersionDeactivate: computed,
            isActiveVersionDeactivate: computed,
            pathToLatestVersion: computed,
            batchId: observable,
            setBatchId: action,
            copyActiveVersionToSelectedVersion: action,
            mlRecommendationData: observable,
            setMlRecommendationData: action,
            aiApps: observable,
            fetchAiApps: action,
            aiDefenseApps: computed,
        });

        reaction(
            () => this.getActiveVersion?.externalId,
            () => {
                void this.loadActiveVersionDataWhenVersionsChanges();
            }
        );
    }

    reset() {
        this.fwVersions = undefined;
        this.selectedFwVersion = undefined;
        this.countriesWithStatusMap = {};
        this.isLoading = false;
        this.isDirty = false;
        this.savingStatus = undefined;
        this.containsActiveVersions = undefined;
        this.applyPolicyTime = undefined;
        this.allFwVersions = {};
        this.protectionType = ProtectionMenuNameOptions.GEO_FILTERING;
    }

    get pathToDraftVersion() {
        return this.buildVersionPathByVersion(this.draftVersion);
    }

    get pathToLatestVersion() {
        return this.buildVersionPathByVersion(this.latestVersion);
    }

    private buildVersionPathByVersion(version: FwVersionInfo | undefined) {
        const { versionProtectionPath } = this;
        if (version?.externalId) {
            const { customerId, serviceId } = this.rootStore.customerStore;
            const versionPath = `version/${version.externalId}/${versionProtectionPath}`;
            return `/${customerId}/${serviceId}/protection/${versionPath}`;
        }
        return '';
    }

    get urlFilteringBlockCategoriesList() {
        return getBlockCategoriesListByVersion(this.selectedFwVersion);
    }

    get isSelectedVersionCommittable() {
        return isVersionDraft(this.selectedFwVersion);
    }

    get isSelectedVersionEditable() {
        if (this.rootStore.userStore.isReadOnlyUser) return false;
        if (!this.selectedFwVersion) return false;
        if (this.selectedFwVersion?.versionType === PolicyType.DEACTIVATE) return false;
        if (this.rootStore.customerStore.isScheduledVersionUpcoming) return false;
        if (this.rootStore.customerStore.isSmartDraftCreationInProgress) return false;
        const isDraftExist = !!this.draftVersion;
        const isCurrentDraft = DRAFT_STATUSES.includes(this.selectedFwVersion.versionStatus);
        const isPendingExist = !!this.pendingVersion;

        const isCurrentActive = isVersionActive(this.selectedFwVersion);
        return isCurrentDraft || (!isDraftExist && !isPendingExist && isCurrentActive);
    }

    get isNotDraftExists() {
        return !this.draftVersion || (this.draftVersion.externalId === this.selectedFwVersion?.externalId);
    }

    get draftVersion() {
        return this.getVersionByStatus(DRAFT_STATUSES);
    }

    get getActiveVersion(): FwVersionInfo | undefined {
        return this.fwVersions?.find(version => VersionState.ACTIVE === version.versionState);
    }

    get versionStateLabel() {
        const selectedVersionInfo = this.fwVersions?.find(version => version.externalId === this.selectedVersionId);
        const versionStateData = versionStatusAndStateMap.get(selectedVersionInfo?.versionState, selectedVersionInfo?.versionStatus);
        return versionStateData ? versionStateData.label : '';
    }

    get selectedVersionId() {
        if (this.selectedFwVersion) {
            return this.selectedFwVersion.externalId;
        }
        return this.fwVersions?.length ? this.fwVersions[0].externalId : undefined;
    }

    get selectedVersionAddressGroups(): AddressGroup[] {
        return getAddressGroupsByVersion(this.selectedFwVersion);
    }

    // the order of conditions is important!
    get getDisableReason() {
        if (this.isSelectedVersionEditable) return undefined;
        if (this.rootStore.userStore.isReadOnlyUser) return DisableReasons.READ_ONLY_USER;
        if (!this.selectedFwVersion) return undefined;
        if (this.selectedFwVersion.versionType === PolicyType.DEACTIVATE) return DisableReasons.DEACTIVATE_VERSION;
        if (this.rootStore.customerStore.isScheduledVersionUpcoming) return DisableReasons.SCHEDULED_VERSION_COME_ON;
        if (this.rootStore.customerStore.isSmartDraftCreationInProgress) return DisableReasons.SMART_DRAFT_IN_PROGRESS;

        const isDraftExist = !!this.draftVersion;
        const isCurrentDraft = DRAFT_STATUSES.includes(this.selectedFwVersion.versionStatus);
        const isPendingExist = !!this.pendingVersion;
        if (isDraftExist && !isCurrentDraft) return DisableReasons.DRAFT_EXIST;
        if (isPendingExist) return DisableReasons.PENDING_EXIST;
        if (isVersionInactive(this.selectedFwVersion)) return DisableReasons.INACTIVE_VERSION;
        return undefined;
    }

    get isSelectedVersionExist() {
        if (!this.selectedFwVersion) return false;
        return !!this.fwVersions?.find(_ => _.externalId === this.selectedFwVersion?.externalId);
    }

    get latestVersion() {
        return this.fwVersions?.[0] || undefined;
    }

    get selectedVersionRules(): Rule[] {
        return getRulesByVersionAndTypeOptional(this.selectedFwVersion);
    }

    get isAllProtAsstStepsDone(): boolean {
        return this.protAsstSteps.every(item => item.selection);
    }

    get rulesCount(): number {
        return this.selectedFwVersion?.content.policy.rules.length || 0;
    }

    get customRulesCount(): number {
        return this.selectedFwVersion?.content.policy.rules.filter(_ => !_.isDefault).length || 0;
    }

    get isOneProtAsstStepNotSame(): boolean {
        return this.protAsstSteps.some(item => !item.isSameBaseVersion);
    }

    get getAddressGroups(): AddressGroup[] {
        return getAddressGroupsByVersion(this.selectedFwVersion);
    }

    get getAllowList(): IP[] {
        return RuleHelper.getAllowList(this.selectedFwVersion);
    }

    get getBlockList(): IP[] {
        return RuleHelper.getBlockList(this.selectedFwVersion);
    }

    get getIpList(): IP[] {
        return [...this.getAllowList, ...this.getBlockList];
    }

    get getDomains(): Domain[] {
        return mapDomains(this.selectedFwVersion?.content?.policy.url_categories);
    }

    get getAddresses(): Address[] {
        return mapAddresses(this.selectedFwVersion?.content?.policy.addresses, this.selectedFwVersion?.content.policy.address_groups);
    }

    get getPureCustomServices(): Service[] {
        return this.selectedFwVersion?.content?.policy.services || [];
    }

    get getProtectionPlusFeatures(): Record<string, string> {
        return this.selectedFwVersion ? RuleHelper.calculateFeaturesState(this.selectedFwVersion) : {};
    }

    get versionProtectionPath() {
        const type = this.protectionType;
        return `${type}${this.calculateSubProtectionPath(type)}`;
    }

    get isInitialVersion() {
        if (this.fwVersions && this.fwVersions?.length > 2) {
            return false;
        }
        const baseVersionExternalId = this.latestVersion?.baseVersionExternalId;
        if (baseVersionExternalId) {
            const parentVersion = this.fwVersions?.find(version => version.externalId === baseVersionExternalId);
            return !parentVersion?.baseVersionExternalId && !isVersionInactive(parentVersion);
        } else {
            return true;
        }
    }

    get isProtAsstOpen() {
        return this.protAsstActIdx > -1;
    }

    get pendingVersion() {
        return this.getVersionByStatus([VersionStatus.PUSH_SENT, VersionStatus.COMMIT_SENT]);
    }

    get managedApps(): AppWithStatus[] {
        const { MyNetworkToInternetAllowedApplications, InternetToMyNetworkAllowedApplications,
            MyNetworkToInternetBlockedApplications, InternetToMyNetworkBlockedApplications } = this.rootStore.ruleStore;

        if (!this.selectedFwVersion ||
            !MyNetworkToInternetBlockedApplications ||
            !InternetToMyNetworkBlockedApplications ||
            !InternetToMyNetworkAllowedApplications ||
            !MyNetworkToInternetAllowedApplications) {
            return [];
        }

        return RuleHelper.getManagedApps(this.selectedFwVersion,
            InternetToMyNetworkAllowedApplications,
            MyNetworkToInternetAllowedApplications,
            InternetToMyNetworkBlockedApplications,
            MyNetworkToInternetBlockedApplications);
    }
    get isOneVersionActive() {
        return this.fwVersions?.length === 1 && isVersionActive(this.fwVersions[0]);
    }

    get isSecondVersionInDraft() {
        return this.fwVersions?.length === 2 && isVersionDraft(this.fwVersions[0]);
    }

    get isVersionDeactivate() {
        return this.selectedFwVersion?.versionType === PolicyType.DEACTIVATE && this.selectedFwVersion.externalId === this.getActiveVersion?.externalId;
    }

    get isActiveVersionDeactivate() {
        return this.getActiveVersion?.versionType === PolicyType.DEACTIVATE;
    }

    private getVersionByStatus(statuses: VersionStatus[]) {
        return this.fwVersions?.find(version => statuses.includes(version.versionStatus));
    }

    private detectActiveVersionChange(oldVersionsList: FwVersionInfo[] | undefined, newVersionsList: FwVersionInfo[]) {
        if (!oldVersionsList) return;
        const oldActiveVersion = oldVersionsList.find(version => version.versionState === VersionState.ACTIVE);
        const newActiveVersion = newVersionsList.find(version => version.versionState === VersionState.ACTIVE);

        if (oldActiveVersion && newActiveVersion &&
            oldActiveVersion.externalId !== newActiveVersion.externalId) {
            const oldStatus = oldVersionsList?.find(_ => _.externalId === newActiveVersion.externalId)?.versionState;
            this.sendFwEvent(EventType.ActiveVersionChanged, {
                actionDuration: this.applyPolicyTime ? calcActionDuration(this.applyPolicyTime) : undefined,
                versionId: newActiveVersion.externalId,
                oldStatus,
                newStatus: newActiveVersion.versionState
            });
        }
    }

    private versionWasUpdated(currentSelectedVersion: FwVersion, updatedVersion: FwVersionInfo): boolean {
        return moment(updatedVersion.updatedTimestamp)
            .isAfter(moment(currentSelectedVersion.metadata.updatedTimestamp));
    }

    private initDataProtAsstStepsDay2Data(isLoadedAgain: boolean, isDraft: boolean) {
        runInAction(() => {
            if (isLoadedAgain) {
                this.protAsstSteps = StepList.map(step => new Step(step));
            }
            // The protection assistant is opened first time
            if (this.protAsstActIdx === -1 || isLoadedAgain) {
                this.protAsstSteps.forEach(step => {
                    if (isDraft) {
                        this.protAsstSteps[step.index].isSameBaseVersion = this.isSameBaseVersionByIndex(step.index, this.baseVersion!);
                    }
                    this.setSelectionByIndex(step.index);
                });
            } else {
                // Each step change
                if (isDraft) {
                    this.protAsstSteps[this.protAsstActIdx].isSameBaseVersion = this.isSameBaseVersionByIndex(this.protAsstActIdx, this.baseVersion!);
                }
                this.protAsstSteps[this.protAsstActIdx].isModified = false;
                this.setSelectionByIndex(this.protAsstActIdx);
            }
        });
    }

    private async initializeBaseVersion() {
        const baseVersionExternalId = this.selectedFwVersion?.baseVersionExternalId;
        if (!baseVersionExternalId) return;

        const baseVersion = await fwVersionService.getVersionData(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId, baseVersionExternalId);
        runInAction(() => {
            this.baseVersion = baseVersion;
        });
    }

    private isSameBaseVersionByIndex(index: number, baseVersion: FwVersion) {
        switch (index) {
            case 0: return this.checkDiffBaseVersionCountries(baseVersion);
            case 1: return this.checkDiffBaseVersionCategories(baseVersion);
            case 2: return this.checkDiffBaseVersionAppServices(baseVersion);
            case 3: return this.checkDiffBaseVersionIPControl(baseVersion);
            case 4: return this.checkDiffBaseVersionProtectionPlus(baseVersion);
            default: return false;
        }
    }

    private setSelectionByIndex(index: number) {
        switch (index) {
            case 0: this.setFilterSelection(0, ProtectionPageName.GeoFiltering); break;
            case 1: this.setFilterSelection(1, ProtectionPageName.WebFiltering); break;
            case 2: this.setFilterSelection(2, ProtectionPageName.AppsServices); break;
            case 3: this.setFilterSelection(3, ProtectionPageName.IPControl); break;
            case 4: this.setFilterSelection(4, ProtectionPageName.ProtectionPlus); break;
        }
    }

    private checkDiffBaseVersionCountries(baseVersion: FwVersion) {
        const baseCountries = RuleHelper.createCountriesWithStatusMap(baseVersion);
        return isEqual(baseCountries, this.countriesWithStatusMap);
    }

    private checkDiffBaseVersionCategories(baseVersion: FwVersion) {
        const baseCategories = getBlockCategoriesListByVersion(baseVersion);
        return isEqual(baseCategories, this.urlFilteringBlockCategoriesList);
    }

    private checkDiffBaseVersionAppServices(baseVersion: FwVersion) {
        const { ruleStore } = this.rootStore;
        const servicesVersion = ruleStore.defaultServicesWithStatus;
        const servicesBaseVersion =
            RuleHelper.getDefaultServicesWithStatus(SystemConfiguration.getInstance().defaultServiceDtoList, baseVersion);
        return isEqual(servicesBaseVersion, servicesVersion);
    }

    private checkDiffBaseVersionIPControl(baseVersion: FwVersion) {
        const blockListEqual = isEqual(RuleHelper.getBlockList(baseVersion), this.getBlockList);
        const allowListEqual = isEqual(RuleHelper.getAllowList(baseVersion), this.getAllowList);
        return blockListEqual && allowListEqual;
    }

    private checkDiffBaseVersionProtectionPlus(baseVersion: FwVersion) {
        // for now - the real changes can be for gold customer only, so I think it's more right indicate that always the changes have been made
        // return _.isEqual(RuleHelper.calculateFeaturesState(baseVersion), this.getProtectionPlusFeatures);
        return false;
    }

    private setFilterSelection(stepNumber: number, protectionPageName: ProtectionPageName) {
        this.setSelectionStepByIndex(stepNumber, this.getBlockInfoFromProtectionPage(protectionPageName, this.selectedFwVersion));
    }

    private setSelectionStepByIndex(stepNumber: number, text: string) {
        runInAction(() => {
            this.protAsstSteps[stepNumber].selection = text;
        });
    }

    // need to investigate if we can replace it with genericCrudMethod and updatePolicyContent
    private async updatePolicyFWVersion(promise: Promise<FwVersionContentAndMetadata>, protectionNameChanged: ProtectionNameAnalytic) {
        try {
            this.sendFwEvent(EventType.UpdateVersion, { protectionNameChanged }); // TODO: need improve type and cover updated from tables
            const res = await Promise.all([promise, delayPromise(LOADER_MINIMUM_MS)]);
            const { content, metadata } = res[0];
            runInAction(() => {
                this.selectedFwVersion = {
                    ...this.selectedFwVersion!,
                    metadata: { ...this.selectedFwVersion!.metadata, ...metadata },
                    content,
                };
                this.setSavingStatus(SavingStatus.Saved);
                if (this.protAsstActIdx > -1 && !this.isInitialVersion) {
                    this.protAsstSteps[this.protAsstActIdx].isModified = true;
                }
            });
        } catch (error) {
            this.setSavingStatus(SavingStatus.Error);
            this.sendFwEvent(EventType.UpdateVersion, { error, logLevel: 'error' });
            throw error;
        }
    }

    private updatePolicyContent(updatedContent: FwVersionContentAndMetadata) {
        runInAction(() => {
            if (this.selectedFwVersion) {
                this.selectedFwVersion = {
                    ...this.selectedFwVersion,
                    metadata: { ...this.selectedFwVersion.metadata, ...updatedContent.metadata },
                    content: updatedContent.content
                }; if (this.protAsstActIdx > -1 && !this.isInitialVersion) {
                    this.protAsstSteps[this.protAsstActIdx].isModified = true;
                }
            }
        });
    }

    private resetSavingStatusTimeout?: NodeJS.Timeout;

    private setLoading(isActive: boolean) {
        runInAction(() => { this.isLoading = isActive; });
    }

    private updateSelectedVersionCountriesMap() {
        const countriesWithStatusMap = RuleHelper.createCountriesWithStatusMap(this.selectedFwVersion!);
        runInAction(() => {
            this.countriesWithStatusMap = countriesWithStatusMap;
        });
    }

    public calculateRulePosition(ruleId: string) {
        const index = this.selectedFwVersion?.content.policy.rules.findIndex(_ => _.id === ruleId);
        return index !== undefined ? index + 1 : undefined;
    }

    public calculateNewRulePosition(ruleId?: string) {
        this.newRulePosition = ruleId ? this.calculateRulePosition(ruleId) : undefined;
    }

    public setProtectionType(protectionType: ProtectionMenuNameOptions) {
        localStorage.setItem(LAST_VISITED_PROTECTION_TAB, protectionType);
        this.protectionType = protectionType;
    }

    public setIpControlType(ipControlType: IPControlSubMenu) {
        this.ipControlType = ipControlType;
    }

    public setWebFilteringType(webFilteringType: WebFilteringSubMenu) {
        this.webFilteringType = webFilteringType;
    }

    public setInventoryType(inventoryType: InventorySubMenu) {
        this.inventoryType = inventoryType;
    }

    public setFirewallRulesMode(mode: 'view' | 'new' | 'edit') {
        this.firewallRulesMode = mode;
    }

    public isDisabledProtectionTypeOnProtAsst(index: number) {
        if (this.isInitialVersion && this.protAsstActIdx > -1) {
            const condition = this.protAsstActIdx < index - 1 && !this.protAsstSteps[index - 1].selection;
            return index >= 2 ? condition : false;
        }
        return false;
    }

    public selectedVersionAddressGroupsByAddress(address: Address): AddressGroup[] {
        return getAddressGroupsByVersionAndAddress(this.selectedFwVersion, address);
    }

    public selectedVersionAddressesByGroup(addressGroup: AddressGroup): Address[] {
        return this.getAddresses.filter(adr => addressGroup.addresses.includes(adr.id));
    }

    public calculateSubProtectionPath(protectionType: ProtectionMenuNameOptions) {
        switch (protectionType) {
            case ProtectionMenuNameOptions.WEB_FILTERING:
                return ('/' + toLowerWithDivider(this.webFilteringType));
            case ProtectionMenuNameOptions.Library:
                return ('/' + toLowerWithDivider(this.inventoryType));
            default: return '';
        }
    }

    public async setIsDirty() {
        if (this.selectedFwVersion?.versionStatus === VersionStatus.PUSH_OK && !this.isDirty) {
            await this.createDraftVersion(this.selectedFwVersion.externalId, this.selectedFwVersion.sid);
            this.isDirty = true;
        }
    }

    public async createDraft(baseExternalId: string, baseSid: number, isRestore = false) {
        return await fwVersionService.createNewDraftVersion(this.rootStore.customerStore.customerId, baseExternalId, baseSid, isRestore);
    }

    public async createDraftVersion(baseExternalId: string, baseSid: number, isRestore = false) {
        try {
            if (!this.rootStore.customerStore.customerId) return;
            this.setLoading(true);
            const newVersion = await this.createDraft(baseExternalId, baseSid, isRestore);
            const versions = await fwVersionService.getVersionsList(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId);
            const countriesWithStatusMap = RuleHelper.createCountriesWithStatusMap(newVersion);
            this.sendEvent(EventType.DraftVersionCreated, { baseExternalId, baseSid, newVersionId: newVersion.externalId, newVersionNumber: newVersion.versionNumber, isRestore });

            runInAction(() => {
                this.countriesWithStatusMap = countriesWithStatusMap;
                this.selectedFwVersion = newVersion;
                this.allFwVersions = versions;
                this.fwVersions = versions[this.rootStore.customerStore.serviceId];
                this.isLoading = false;
            });
        } catch (e) {
            this.sendEvent(EventType.DraftVersionCreated, { logLevel: 'error', baseExternalId, baseSid, isRestore, error: e, });
            this.setLoading(false);
        }
    }

    public async restorePolicyAndApply(baseVersionExternalId: string, name?: string, comments?: string) {
        const { customerId, serviceId } = this.rootStore.customerStore;
        const version = await fwVersionService.restorePolicyAndApply(customerId, serviceId, baseVersionExternalId, name, comments);
        this.updateSelectedVersionInStore(version);
        await this.loadVersionsList();
    }

    public async pullPolicyAndApply(sourceServiceId: number, sourceServiceName: string, name?: string, comments?: string) {
        const { customerId, serviceId } = this.rootStore.customerStore;
        const version = await fwVersionService.pullPolicyAndApply(customerId,
            serviceId,
            sourceServiceId,
            sourceServiceName,
            name,
            comments);
        this.updateSelectedVersionInStore(version);
        await this.loadVersionsList();
    }

    public async loadVersionsList() {
        const oldVersionsList = this.fwVersions;
        const res = await fwVersionService.getVersionsList(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId);
        const serviceVersions = res[this.rootStore.customerStore.serviceId];

        runInAction(() => {
            this.allFwVersions = res;
            if (!isEqual(serviceVersions, this.fwVersions)) {
                this.fwVersions = serviceVersions;
                this.detectActiveVersionChange(oldVersionsList, serviceVersions);
                if (!this.containsActiveVersions) {
                    this.containsActiveVersions = this.fwVersions.some(version => version.versionState === VersionState.ACTIVE);
                }
                if (this.selectedFwVersion) {
                    const updatedSelectedVersionInfo = serviceVersions.find(version => version.externalId === this.selectedFwVersion!.externalId);
                    if (updatedSelectedVersionInfo && this.versionWasUpdated(this.selectedFwVersion, updatedSelectedVersionInfo)) {
                        void this.loadVersionData(updatedSelectedVersionInfo.externalId, false);
                    }
                }
            }
        });
    }

    public async commitAndPushVersion(data: PolicyActionDto) {
        await this.setIsDirty();
        try {
            this.setSavingStatus(SavingStatus.Applying);
            this.applyPolicyTime = performance.now();
            const pushedVersion = await this.handleVersionPush(data);
            this.setSavingStatus(undefined);
            runInAction(() => {
                if (this.latestVersion) { // convert version label to pending if it succeeded to apply version
                    this.latestVersion.versionStatus = pushedVersion.versionStatus;
                    if (this.selectedFwVersion?.externalId === this.latestVersion.externalId)
                        this.selectedFwVersion = pushedVersion;
                }
                if (data.selectedConnections?.length) {
                    this.setBatchId(`${this.rootStore.customerStore.serviceId}:${pushedVersion.batchId}`);
                }
            });
        }
        catch {
            this.setSavingStatus(SavingStatus.Error);
        }
        void this.loadVersionsList();
        if (this.selectedVersionId) {
            void this.loadVersionData(this.selectedVersionId);
        }
        void this.rootStore.notificationStore.loadNotifications();
    }

    private async handleVersionPush(data: PolicyActionDto): Promise<FwVersion> {
        const { policyName, policyComments, scheduleDate, selectedConnections } = data;
        const { customerId, serviceId } = this.rootStore.customerStore;
        const latestExternalId = this.latestVersion?.externalId ?? '';

        let result: FwVersion;
        const isScheduled = !!scheduleDate;
        const isPushed = !!selectedConnections?.length;
        const scheduleTime = isScheduled ? moment(scheduleDate).utc().format() : undefined;
        const numberOfSids = isPushed ? selectedConnections?.length : undefined;

        if (!scheduleDate) {
            result = !isPushed
                ? await fwVersionService.commitAndPushVersion(customerId, serviceId, latestExternalId, policyName, policyComments)
                : await fwVersionService.applyPolicyWithOtherConnections(customerId, serviceId, latestExternalId, selectedConnections ?? [], policyName, policyComments);
        } else {
            result = isPushed
                ? await fwVersionService.scheduleMultipleConnections(customerId, serviceId, latestExternalId,
                    selectedConnections ?? [], scheduleTime ?? '', policyName, policyComments)
                : await fwVersionService.scheduleSinglePolicy(customerId, serviceId, latestExternalId, scheduleTime ?? '', policyName, policyComments);
            await this.rootStore.customerStore.getServiceInfo();
        }

        this.sendApplyPolicyEvent(false, latestExternalId, { isPushed, isScheduled, scheduleDate: scheduleTime, numberOfSids });

        return result;
    }

    public async discardSchedule() {
        const { customerId, serviceId } = this.rootStore.customerStore;
        await fwVersionService.discardSchedulePolicy(customerId, serviceId);
        await this.rootStore.customerStore.getServiceInfo();
    }

    public async loadActiveVersionDataWhenVersionsChanges() {
        const version = this.getActiveVersion;
        if (version && version.serviceLevel !== LevelName.BRONZE.toLowerCase()) {
            try {
                const activeVersionData = await this.getVersionData(version.externalId);
                runInAction(() => {
                    this.activeVersionData = activeVersionData;
                });
            } catch (e) {
                // non-critical background fetch — log and continue
                console.error('loadActiveVersionDataWhenVersionsChanges failed', e);
            }
        }
    }

    public async getVersionData(externalId: string) {
        return await fwVersionService.getVersionData(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId, externalId);
    }

    public async loadVersionData(externalId: string, setIsLoading = true) {
        try {
            this.setLoading(setIsLoading);
            const res = await this.getVersionData(externalId);
            this.updateSelectedVersionInStore(res);
        } catch (e) {
            this.setLoading(false);
            throw e;
        }
    }

    public async deleteVersion(externalId: string) {
        try {
            this.setLoading(true);
            await fwVersionService.deleteVersion(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId, externalId);
            await this.rootStore.customerStore.getServiceInfo();
            await this.loadVersionsList();
            runInAction(() => {
                if (externalId === this.selectedFwVersion?.externalId) {
                    this.selectedFwVersion = undefined;
                }
                this.isDirty = false;
            });
            this.setLoading(false);
        } catch {
            this.setLoading(false);
        }
    }

    public async genericVersionActionMethod(args: any[], callback: (
        customerId: number, serviceId: number, ...args: any[]) => Promise<FwVersion | void>
    ): Promise<any> {
        if (!this.selectedFwVersion || !this.rootStore.customerStore.customerId) return;
        try {
            this.setLoading(true);
            const res = await callback(
                this.rootStore.customerStore.customerId,
                this.rootStore.customerStore.serviceId,
                ...args
            );
            await this.loadVersionsList();
            this.setLoading(false);
            if (res) return res;
        } catch (e) {
            this.setLoading(false);
            this.setSavingStatus(SavingStatus.Error);
            throw e;
        }
    }

    public async pull(sourceServiceId: number | undefined, sourceCircuitId: string | undefined) {
        if (!this.rootStore.customerStore.customerId || !sourceServiceId || !sourceCircuitId) return;
        try {
            this.setLoading(true);
            await fwVersionService.pullVersion(
                this.rootStore.customerStore.customerId,
                this.rootStore.customerStore.serviceId, sourceServiceId, sourceCircuitId
            );
            await this.loadVersionsList();
            this.setLoading(false);

        } catch (e) {
            this.setLoading(false);
            this.setSavingStatus(SavingStatus.Error);
            throw e;
        }
    }

    public async deactivateReactivateVersion(isDeactivate: boolean) {
        try {
            this.setLoading(true);
            const updatedContent = await fwVersionService.activateVersion(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId, isDeactivate);
            this.updatePolicyContent(updatedContent);
            await this.loadVersionsList();
            if (isDeactivate) {
                await this.rootStore.customerStore.getServiceInfo();
            }
            this.setLoading(false);
        } catch {
            this.setLoading(false);
        }
    }

    public updateSelectedVersionInStore(version: FwVersion) {
        const countriesWithStatusMap = RuleHelper.createCountriesWithStatusMap(version);
        runInAction(() => {
            this.countriesWithStatusMap = countriesWithStatusMap;
            this.selectedFwVersion = version;
            this.isLoading = false;
            this.isDirty = false;
        });
    }

    public async updateCategoryStatus(blockCategoryList: any) {
        const externalId = this.selectedFwVersion?.externalId;
        const profileName = this.selectedFwVersion?.content?.policy.url_filtering?.[0].displayName;
        if (!externalId || !profileName) {
            return;
        }

        this.setSavingStatus(SavingStatus.Saving);
        const blockCategoryPromise: Promise<FwVersionContentAndMetadata> = fwVersionService.updateBlockCategory(this.rootStore.customerStore.customerId,
            this.rootStore.customerStore.serviceId, externalId, profileName, blockCategoryList);
        await this.updatePolicyFWVersion(blockCategoryPromise, WebFilteringSubMenu.CATEGORIES);
    }

    public async updateCountryStatus(codeCountry: string, status: boolean, blockDirectionType: BlockDirectionType) {
        const externalId = this.selectedFwVersion?.externalId;
        if (!externalId || !this.rootStore.customerStore.customerId) return;
        this.setSavingStatus(SavingStatus.Saving);
        if (status) {
            delete this.countriesWithStatusMap[codeCountry][blockDirectionType];
            // Delete empty object
            if (Object.keys(this.countriesWithStatusMap[codeCountry]).length === 0) {
                delete this.countriesWithStatusMap[codeCountry];
            }
        } else {
            this.countriesWithStatusMap[codeCountry] = { ...this.countriesWithStatusMap[codeCountry], [blockDirectionType]: true };
        }

        const updatedCountryDto = mapToArray(this.countriesWithStatusMap, ([key, value]) => ({
            countryCode: key,
            countryDirectionsValueDto: value
        }));

        const updateReqPromise = fwVersionService.updateBlockCountry(this.rootStore.customerStore.customerId,
            this.rootStore.customerStore.serviceId, externalId, updatedCountryDto);
        await this.updatePolicyFWVersion(updateReqPromise, ProtectionMenuNameOptions.GEO_FILTERING);
    }

    public async updateDefaultServiceStatus(appServicesUpdate: ServicesUpdateDto) {
        if (!this.selectedFwVersion || !this.rootStore.customerStore.customerId) return;
        this.setSavingStatus(SavingStatus.Saving);
        const updateReqPromsie = fwVersionService.updateDefaultServiceAndApp(this.rootStore.customerStore.customerId,
            this.rootStore.customerStore.serviceId, this.selectedFwVersion.externalId, appServicesUpdate);

        await this.updatePolicyFWVersion(updateReqPromsie, ProtectionMenuNameOptions.SERVICES);
    }

    public async initProtAsstStepsDay2Data(isVersionChanged = false) {
        const isDraft = isVersionDraft(this.selectedFwVersion);
        let isLoadedAgain = isVersionChanged;
        if (isDraft) {
            const isSameBaseVersion = this.baseVersion?.externalId === this.selectedFwVersion?.baseVersionExternalId;
            if (!this.baseVersion || !isSameBaseVersion) {
                isLoadedAgain = true;
                await this.initializeBaseVersion();
            }
        }
        this.initDataProtAsstStepsDay2Data(isLoadedAgain, isDraft);
    }

    public async setProtAsstActIdx(index: number) {
        if (index === this.protAsstActIdx) return;

        // The protection assistant will be open/change
        if (index > -1) {
            // Day 1
            if (this.isInitialVersion) {
                this.setSelectionByIndex(this.protAsstActIdx);
            } else {
                // Day 2
                await this.initProtAsstStepsDay2Data();
            }
        }

        runInAction(() => {
            this.protAsstActIdx = index;
        });
    }

    public getBlockInfoFromProtectionPage(protectionPage: ProtectionPageName, fwVersion: FwVersion | undefined) {
        return calcBlockInfo(this.rootStore)(protectionPage, fwVersion);
    }

    public getRuleById(ruleId: string): Rule | undefined {
        return getRuleById(this.selectedFwVersion, ruleId);
    }

    public async createNewVersionWithAllRecommendations(isApply: boolean, baseVersion?: FwVersion) {
        const baseVersionForNewDraft = baseVersion || this.fwVersions?.find(_ => _.versionState === VersionState.ACTIVE);
        if (!baseVersionForNewDraft) return;

        if (!this.draftVersion) {
            await this.createDraftVersion(baseVersionForNewDraft.externalId, baseVersionForNewDraft.sid);
            // after create draft version now we must have draft version
            if (!this.draftVersion) {
                console.error('create a new version failed! we can`t apply recommendations');
                throw new Error('create a new version failed! we can`t apply recommendations');
            }
        }

        this.setLoading(true);
        const types = [BulkRecommendationType.COUNTRY, BulkRecommendationType.CATEGORY,
        ...(this.rootStore.customerStore.selectedLocation?.serviceLevel === LevelName.GOLD ? [BulkRecommendationType.ATP_PROFILES] : [])];
        try {
            if (isApply) {
                this.applyPolicyTime = performance.now();
            }
            await fwVersionService.bulkUpdatePolicyBasedOnRecommendation(this.rootStore.customerStore.customerId, this.draftVersion.sid, {
                externalId: this.draftVersion.externalId,
                bulkRecommendationTypeList: types,
                toApply: isApply
            });

            void this.loadVersionsList();
        } catch (error) {
            console.error('Failed to apply recommendations: ', error);
            this.applyPolicyTime = undefined;
        }
        this.setLoading(false);
    }

    public setSavingStatus(status: SavingStatus | undefined) {
        runInAction(() => {
            this.savingStatus = status;

            if (this.resetSavingStatusTimeout) {
                clearTimeout(this.resetSavingStatusTimeout);
            }

            if (status === SavingStatus.Saved || status === SavingStatus.Applied) {
                this.resetSavingStatusTimeout = setTimeout(() => {
                    runInAction(() => {
                        this.savingStatus = undefined;
                    });
                }, 3000);
            }
        });
    }

    public handleBulkUpdateRecommendationOption = async (item: PolicyMenuItem) => {
        await this.setIsDirty();
        if (!this.selectedFwVersion || !this.rootStore.customerStore.customerId) return;

        try {
            this.setSavingStatus(SavingStatus.Saving);
            const bulkPromise = fwVersionService.bulkUpdatePolicyBasedOnRecommendation(
                this.rootStore.customerStore.customerId,
                this.selectedFwVersion.sid, {
                externalId: this.selectedFwVersion.externalId,
                bulkRecommendationTypeList: [item.updateType as BulkRecommendationType],
                toApply: false
            });

            await this.updatePolicyFWVersion(bulkPromise, item.updateType as BulkRecommendationType);
            if (item.updateType === BulkRecommendationType.COUNTRY) {
                this.updateSelectedVersionCountriesMap();
            }
        } catch (e) {
            this.setSavingStatus(SavingStatus.Error);
            throw e;
        }
    };

    public handleBulkUpdateOptionSelected = async (item: PolicyMenuItem) => {
        await this.setIsDirty();
        if (!this.selectedFwVersion || !this.rootStore.customerStore.customerId) return;

        try {
            this.setSavingStatus(SavingStatus.Saving);
            const bulkPromise = fwVersionService.bulkUpdatePolicyBasedOnSelection(
                this.selectedFwVersion.customerId,
                this.selectedFwVersion.sid,
                {
                    externalId: this.selectedFwVersion.externalId,
                    bulkUpdateType: item.updateType as BulkUpdateType,
                    bulkUpdateAction: item.updatedAction as BulkUpdateAction,
                    direction: item.updateDirection as BulkUpdateDirection
                });

            await this.updatePolicyFWVersion(bulkPromise, item.updateType as BulkUpdateType);
            if (item.updateType === BulkUpdateType.COUNTRY) {
                this.updateSelectedVersionCountriesMap();
            }
        } catch (e) {
            this.setSavingStatus(SavingStatus.Error);
            throw e;
        }
    };

    public async genericCrudMethod(
        callback: (customerId: number, sid: number, versionId: string, ...args: any[]) => Promise<FwVersionContentAndMetadata>,
        args: any[]
    ): Promise<Promise<{ void: any }> | undefined> {
        await this.setIsDirty();
        if (!this.selectedFwVersion || !this.rootStore.customerStore.customerId) return;

        try {
            this.setLoading(true);
            const updatedContent = await callback(
                this.rootStore.customerStore.customerId,
                this.selectedFwVersion.sid,
                this.selectedFwVersion.externalId,
                ...args
            );
            this.updatePolicyContent(updatedContent);
        } finally {
            this.setLoading(false);
        }
    }

    public sendApplyPolicyEvent(isRecommended: boolean,
        versionId?: string,
        extraInfo?: object): void {
        this.sendFwEvent(EventType.ApplyPolicy, { versionId, isRecommended, ...extraInfo });
    }

    public sendFwEvent(eventType: EventType, extraInfo?: object): void {
        const { externalId, versionNumber, versionState, versionStatus } = this.rootStore.fwVersionStore.selectedFwVersion || {};
        this.sendEvent(eventType, { externalId, versionNumber, versionState, versionStatus, ...extraInfo });
    }

    async addManagedApps(appsList: string[]) {
        await this.setIsDirty();

        const externalId = this.selectedFwVersion?.externalId;
        if (!externalId) return;
        const addAppPromise = managedAppsService.addManagedApps(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId, externalId, appsList);
        await this.updatePolicyFWVersion(addAppPromise, ProtectionMenuNameOptions.APPS);
    }

    public searchApps = async (searchStr: string) => {
        if (!this.selectedFwVersion) return [];
        return await managedAppsService.searchApps(searchStr);
    };

    public async fetchAiApps() {
        const aiApps = await managedAppsService.getAiApps();
        runInAction(() => {
            this.aiApps = aiApps;
        });
    }

    get aiDefenseApps(): AppWithStatus[] {
        const managedAppsMap = new Map(this.managedApps.map(app => [app.id, app]));
        return this.aiApps.map(aiApp => {
            const managed = managedAppsMap.get(aiApp.applicationMetadata.id);
            return {
                id: aiApp.applicationMetadata.id,
                displayName: aiApp.applicationMetadata.displayName || aiApp.applicationMetadata.id,
                description: aiApp.applicationMetadata.description,
                dependencies:aiApp.dependencies,
                inboundAllowed: managed ? managed.inboundAllowed : this.rootStore.ruleStore.isAppsInboundAllowAll,
                outboundAllowed: managed ? managed.outboundAllowed : this.rootStore.ruleStore.isAppsOutboundAllowAll,
            } as AppWithStatus;
        });
    }

    public async changeAppStatus(appId: string, direction: Direction, isAllowed: boolean): Promise<FwVersionContent | undefined> {
        if (!this.selectedFwVersion || !this.rootStore.customerStore.customerId) return undefined;
        this.setSavingStatus(SavingStatus.Saving);

        const updateReqPromise = managedAppsService.updateAppStatus(this.rootStore.customerStore.customerId,
            this.rootStore.customerStore.serviceId, this.selectedFwVersion.externalId, appId, direction, isAllowed);

        await this.updatePolicyFWVersion(updateReqPromise, ProtectionMenuNameOptions.APPS);
        return this.selectedFwVersion.content;
    }

    public async getPolicySummary(externalId: string, serviceId = this.rootStore.customerStore.serviceId) {
        const { customerStore } = this.rootStore;
        return await fwVersionService.getPolicySummary(customerStore.customerId, serviceId, externalId);
    }

    public setRecommendedAppData(data: RecommendedAppData | undefined) {
        this.recommendedAppData = data;
    }

    public setMlRecommendationData(data: MlRecommendationData | undefined) {
        this.mlRecommendationData = data;
    }

    public async getAvailableConnectionsForPushPolicy(circuitId: number | undefined, serviceId: number | undefined, compareActiveVersion: boolean, isScheduled: boolean) {
        if (!circuitId || !serviceId) return;
        return await fwVersionService.getAvailableConnectionsForPushPolicy(circuitId, serviceId, compareActiveVersion, isScheduled);
    }

    public async pushPolicy(circuitId: number | undefined, sid: number | undefined, selectedConnections: number[], toApply: boolean) {
        if (!circuitId || !sid) return;
        const res = await fwVersionService.pushPolicy(circuitId, sid, selectedConnections, toApply);
        runInAction(() => this.setBatchId(`${sid}:${res}`));
        return res;
    }

    public setBatchId(batchId: string | null) {
        this.batchId = batchId;
        if (batchId) {
            localStorage.setItem(PUSH_POLICY_BATCH_ID, batchId);
        } else {
            localStorage.removeItem(PUSH_POLICY_BATCH_ID);
        }
    }

    public copyActiveVersionToSelectedVersion() {
        if (this.activeVersionData) {
            this.updateSelectedVersionInStore(this.activeVersionData);
        }
    }
}
